import { parseAsBoolean, useQueryStates } from 'nuqs';
import { useCallback, useMemo } from 'react';
import type { FilterField } from './filters/filterUtils';
import {
  buildFilterParsers,
  buildGraphQLFilter,
  clearPayload,
  hasActiveFilters,
  normalizeFilters,
} from './filters/filterUtils';

type QueryParamParser<Type> = {
  parse: (value: string | null) => Type;
  serialize: (value: Type) => string;
  defaultValue?: Type;
};

export interface AlarmFilters {
  name: string;
  machines: string[];
  signals: string[];
}

const fields: Array<FilterField<keyof AlarmFilters>> = [
  { key: 'name', type: 'search', graphKey: 'search' },
  { key: 'machines', type: 'multi', graphKey: 'machineIds' },
  { key: 'signals', type: 'multi' },
];

const filterParsers = buildFilterParsers<keyof AlarmFilters>(fields) as {
  [K in keyof AlarmFilters]: QueryParamParser<AlarmFilters[K]>;
};

// Separate parser for allUsers toggle (not part of the generic filter system).
// Default false = only my alarms (EDMS default). When true, sends allUsers=true to EDMS.
const allUsersParsers = {
  showAll: parseAsBoolean.withDefault(false),
};

/**
 * Hook to manage alarm filters via URL query params.
 * This is the single source of truth for alarm filter state.
 * No prop drilling needed - use this hook anywhere you need alarm filters.
 */
export function useAlarmFilters() {
  const [filters, setUrlState] = useQueryStates(filterParsers);
  const [showAllState, setShowAllState] = useQueryStates(allUsersParsers);

  /** When true, alarms from all users are shown. When false, only the current user's alarms. */
  const showAllUsers = showAllState.showAll;

  const normalizedFilters = useMemo(
    () => normalizeFilters<AlarmFilters, keyof AlarmFilters>(filters, fields),
    [filters]
  );

  const setName = useCallback(
    (value: string) => {
      setUrlState({ name: value || null });
    },
    [setUrlState]
  );

  const setMachines = useCallback(
    (values: string[]) => {
      setUrlState({ machines: values.length > 0 ? values : null });
    },
    [setUrlState]
  );

  const setSignals = useCallback(
    (values: string[]) => {
      setUrlState({ signals: values.length > 0 ? values : null });
    },
    [setUrlState]
  );

  const setShowAllUsers = useCallback(
    (value: boolean) => {
      setShowAllState({ showAll: value || null });
    },
    [setShowAllState]
  );

  const setFilters = useCallback(
    (newFilters: Partial<AlarmFilters>) => {
      const currentMachines = Array.isArray(filters.machines)
        ? filters.machines
        : [];
      const currentSignals = Array.isArray(filters.signals)
        ? filters.signals
        : [];

      setUrlState({
        name:
          newFilters.name !== undefined
            ? newFilters.name || null
            : filters.name || null,
        machines:
          newFilters.machines !== undefined
            ? newFilters.machines.length > 0
              ? newFilters.machines
              : null
            : currentMachines.length > 0
              ? currentMachines
              : null,
        signals:
          newFilters.signals !== undefined
            ? newFilters.signals.length > 0
              ? newFilters.signals
              : null
            : currentSignals.length > 0
              ? currentSignals
              : null,
      });
    },
    [setUrlState, filters]
  );

  const clearFilters = useCallback(() => {
    setUrlState(clearPayload(fields));
  }, [setUrlState]);

  const active = useMemo(() => hasActiveFilters(filters, fields), [filters]);

  const graphQLFilter = useMemo(() => {
    const base = buildGraphQLFilter(filters, fields);
    // Merge allUsers into the GraphQL filter when showing all users' alarms
    if (showAllUsers) {
      return { ...base, allUsers: true };
    }
    return base;
  }, [filters, showAllUsers]);

  return {
    filters: normalizedFilters,
    showAllUsers,
    setName,
    setMachines,
    setSignals,
    setShowAllUsers,
    setFilters,
    clearFilters,
    hasActiveFilters: active,
    graphQLFilter,
  };
}
